import { useMemo } from 'react';
import { useTracking } from 'react-tracking';

import { InformationSolid } from '@simplywallst/ui-core/icons';
import { Button, Heading, Text, Grid, Box } from '@simplywallst/ui-core';

import { pluralise } from '@/utilities/pluralise';
import { formatterCurrency } from '@/utilities/format';

import Tooltip from '@/components/Tooltip';

import { STOCK_VALUATOR_SITE } from '@/constants';

import type { WatchlistItem } from '@/hooks/useWatchlist';
import { useFairValueContext } from './Context';
import { CashValuationTooltip } from './Tooltip';
import {
  ListContainer,
  ListItem,
  ValuatorLink,
  UnderlineButton,
} from './styled';

interface Props {
  disabled: boolean;
  company: WatchlistItem['company'];
  onDefaultSelect: (value: number) => void;
}

const GuidanceList = ({ disabled, company, onDefaultSelect }: Props) => {
  const { trackEvent } = useTracking();
  const { actions } = useFairValueContext();

  const { netIncome } = company.analysisHealth;
  const { trading_item_currency_iso: companyCurrencyISO } =
    company.data.currencyInfo;
  const { npvPerShare, priceTarget, priceTargetAnalystCount } =
    company.analysisValue;

  const isProfitable = (netIncome ?? 0) > 0;

  const valuatorURL = useMemo(() => {
    const [, path = ''] = company.canonicalURL.split('/stocks/');
    return `${STOCK_VALUATOR_SITE}/${path}`;
  }, [company]);

  const handleFairValueSelection = (value: number | null, source: string) => {
    if (value === null) {
      return;
    }
    onDefaultSelect(value);
    trackEvent({
      action: 'click',
      modifier: source,
    });
  };

  return (
    <ListContainer>
      <Heading
        as="h4"
        opacity={0.7}
        color="spaceGrayRGBA"
        typography="xSmallStrong"
      >
        Fair Value Guidance
      </Heading>
      <ul>
        <ListItem>
          <Grid
            template="auto auto / min-content auto max-content"
            alignItems="center"
            columnGap="8px"
          >
            <Tooltip
              title={<CashValuationTooltip />}
              placement="top"
              zIndex={9999}
            >
              <InformationSolid opacity={0.5} />
            </Tooltip>
            <Text typography="xSmall" color="spaceGray">
              Discounted Cash Flow Valuation
            </Text>
            {npvPerShare ? (
              <Button
                styleType="strongLink"
                disabled={disabled}
                onClick={() => {
                  handleFairValueSelection(npvPerShare, 'set-DCF-valuation');
                }}
              >
                <Text as="span" typography="xSmall">
                  {formatterCurrency(npvPerShare, companyCurrencyISO)}
                </Text>
              </Button>
            ) : (
              <Text
                typography="smallStrong"
                color="spaceGrayRGBA"
                opacity={0.3}
              >
                no data
              </Text>
            )}
            <div role="presentation" />
            <UnderlineButton
              styleType="softLink"
              disabled={disabled}
              display="block"
              onClick={() => {
                actions.openValuationModal();
                trackEvent({
                  action: 'click',
                  modifier: 'view raw data',
                });
              }}
            >
              <Text
                as="span"
                typography="tiny"
                color="spaceGrayRGBA"
                opacity={0.5}
              >
                View Data
              </Text>
            </UnderlineButton>
          </Grid>
        </ListItem>
        <ListItem>
          <Grid
            template="auto auto / min-content auto max-content"
            alignItems="center"
            columnGap="8px"
          >
            <Tooltip
              title={`Average price target from ${pluralise(
                priceTargetAnalystCount,
                'Analyst',
                'Analysts'
              )}`}
              placement="top"
              zIndex={9999}
            >
              <InformationSolid opacity={0.5} />
            </Tooltip>
            <Text typography="xSmall" color="spaceGray">
              Analyst Consensus Price Target
            </Text>
            {priceTarget ? (
              <Button
                styleType="strongLink"
                disabled={disabled}
                onClick={() =>
                  handleFairValueSelection(
                    priceTarget,
                    'set-analysts-valuation'
                  )
                }
              >
                <Text as="span" typography="xSmall">
                  {formatterCurrency(priceTarget, companyCurrencyISO)}
                </Text>
              </Button>
            ) : (
              <Text
                typography="smallStrong"
                color="spaceGrayRGBA"
                opacity={0.3}
              >
                no data
              </Text>
            )}
            <div role="presentation" />
            <UnderlineButton
              styleType="softLink"
              disabled={disabled}
              display="block"
              onClick={() => {
                trackEvent({
                  action: 'click',
                  modifier: 'view-analysts',
                });
                if (priceTargetAnalystCount) {
                  actions.openAnalystsModal();
                }
              }}
            >
              <Text
                as="span"
                typography="tiny"
                color="spaceGrayRGBA"
                opacity={0.5}
              >
                {priceTargetAnalystCount}{' '}
                {pluralise(priceTargetAnalystCount, 'Analyst', 'Analysts')}
              </Text>
            </UnderlineButton>
          </Grid>
        </ListItem>
        {isProfitable && (
          <ListItem>
            <ValuatorLink
              as="a"
              target="_blank"
              rel="noreferrer noopener nofollow"
              href={valuatorURL}
              gap="8px"
              alignItems="center"
              template="min-content / max-content min-content"
            >
              <Text as="span" typography="xSmall" color="spaceGray">
                Use Valuation Calculator
              </Text>
              <Box
                as="span"
                backgroundColor="paper"
                borderRadius="x0_25"
                paddingX="x0_5"
                paddingY="x0_25"
              >
                <Text
                  typography="tiny"
                  color="spaceGrayRGBA"
                  textTransform="uppercase"
                  opacity={0.5}
                >
                  Beta
                </Text>
              </Box>
            </ValuatorLink>
          </ListItem>
        )}
      </ul>
    </ListContainer>
  );
};

export default GuidanceList;
